import { ADD_PRODUCTS_ROUTE, ADD_PRODUCTS_SEARCH_ROUTE } from '@mwe/constants';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { NewWlVoucherFormComponent } from '../new-wl-voucher-form/new-wl-voucher-form.component';
import { ValidationErrors, Validators } from '@angular/forms';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { FormComponent } from '../../form/form.component';
import { InfoboxComponent } from '../../infobox/infobox.component';
import { InlineNotificationComponent } from '../../inline-notification/inline-notification.component';
import { LinkListGroupComponent } from '../../list/link-list-group/link-list-group.component';
import { mapToErrorItems } from '../../form/validators/map-error-key.validator';
import { NewVoucherStore } from '../new-voucher.store';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  capitalizeFirstLetter,
  checkCodeStatus,
  CODE_CONTROL_NAME,
  CodeStatus,
  CodeValidity,
  createAdditionalFields,
  createBody,
  createNewVoucherWarning,
  createProcessILError,
  deleteCodeStatus,
  errorCodeValidity,
  ERRORS,
  ErrorType,
  initWithConfig,
  isEnergiegutschein,
  isTarifwechselOrKundeWerden,
  isWienerLinienVoucher,
  NewVoucherWarning,
  validCodeValidity,
} from '../new-voucher.util';
import {
  Aktionscode,
  CommonMappingData,
  FormInputErrorItem,
  IAktionen,
  IFormModel,
  TariffSelectionCardData,
  ZusatzInformationen,
} from '@mwe/models';
import { INewVoucherFormConfig } from '@mwe/services';
import { NgTemplateOutlet } from '@angular/common';

// TODO: Tests for everything related

const DELIMITER = ': ';

@Component({
  selector: 'mwe-new-voucher-form',
  imports: [
    NewWlVoucherFormComponent,
    BasicCardComponent,
    FormComponent,
    InfoboxComponent,
    InlineNotificationComponent,
    LinkListGroupComponent,
    TranslatePipe,
    NgTemplateOutlet,
  ],
  templateUrl: './new-voucher-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewVoucherFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly newVoucherStore = inject(NewVoucherStore);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);

  readonly inputConfig = input.required<INewVoucherFormConfig>(); /** DO NOT USE, USE this.config() INSTEAD **/
  readonly formRef = viewChild<FormComponent>('form');

  readonly voucherDetail = signal<Aktionscode>(undefined); // Single source of truth - do not use this.newVoucherStore.voucherDetails()
  readonly formModel = signal<IFormModel>(undefined);
  readonly code = signal<string>(''); // Input of user
  readonly codeStatus = signal<CodeStatus>('check'); // Status if the cade can be checked or deleted
  readonly codeValidity = signal<CodeValidity>(undefined); // Validity of code to display inline-notifications
  readonly wienerLinienVoucherDetail = signal<Aktionscode>(undefined); // Used to for logic in NewWlVoucherFormComponent
  readonly isWienerLinienVoucher = signal<boolean>(undefined); // Is required because of HTML display
  readonly additionalFields = signal<ZusatzInformationen[]>(undefined); // Used for HTTP request when voucher is from Wiener Linien
  readonly selectedTariffs = signal<TariffSelectionCardData[]>(undefined); // Manually set selected tarifs
  readonly validMessageKeys = signal<string[]>([]); // Infotexts of vouchers
  readonly inputHTMLElement = signal<HTMLElement>(undefined); // Used for hacky CSS manipulation
  readonly asyncLoadingFuncLock = signal<boolean>(undefined); // Used to reduce calling of function

  readonly config = computed<INewVoucherFormConfig>(() => initWithConfig(this.inputConfig()));
  readonly codeCheckDisabled = computed<boolean>(() => this.code()?.length === 0);
  readonly errorItems = computed<FormInputErrorItem[]>(() => mapToErrorItems(this.getAllErrors()));
  readonly warningItem = computed<NewVoucherWarning>(() => this.newVoucherStore.voucherDetailWarning());

  /** ===== Setup ===== **/

  ngOnInit(): void {
    this.newVoucherStore.resetCounter();
    this.formModel.set(this.initFormModel());
  }

  ngAfterViewInit(): void {
    this.inputHTMLElement.set(document.getElementById(CODE_CONTROL_NAME));
  }

  ngOnDestroy(): void {
    this.newVoucherStore.updateIsVisible(false, true);
    this.newVoucherStore.unblockVisibility();
  }

  /** ===== Effects from this or store ===== **/

  constructor() {
    /** ===== Initialization ===== **/
    // When a predefinedCode exists in voucher-redeem, initiate the validation
    let energieCounter = 0; // used to react to specific edge-cases
    effect(() => {
      const config = this.config();
      const formRef = this.formRef();
      if (!config.predefinedCode || !isEnergiegutschein(config.processType) || !formRef) return;

      const activeProducts = this.newVoucherStore.productStore.activeProducts();
      if (!activeProducts || activeProducts.length === 0) return;
      if (energieCounter++ >= 1) return;

      this.code.set(config.predefinedCode);

      untracked(() => formRef.triggerDefaultFormInputSpinnerSubmit());
    });

    let notEnergieFormCounter = 0; // this.formRef initially emits twice, so this counter is necessary to avoid multiple initial calls
    effect(async () => {
      const config = this.config();
      const predefinedCode = config.predefinedCode;
      const code = untracked(() => this.code());
      const codeStatus = untracked(() => this.codeStatus());
      if (!predefinedCode) return;

      const fun = (async (formRef: FormComponent): Promise<void> => {
        if (!code || !checkCodeStatus(codeStatus) || !formRef) return;

        this.code.set(predefinedCode); // binding necessary because of this.code

        this.asyncLoadingFuncLock.set(true);
        return formRef?.triggerDefaultFormInputSpinnerSubmit().then(() => this.asyncLoadingFuncLock.set(false));
      }).bind(this);

      if (isTarifwechselOrKundeWerden(config.processType)) {
        const formRef = this.formRef();
        if (notEnergieFormCounter++ === 1) fun(formRef);
      }
    });

    /** ===== Voucher-Redeem effects ===== **/
    // When products are loaded and this.newVoucherStore.voucherDetail was fetched, filter the result and display any warnings
    effect(() => {
      const voucherDetail = this.newVoucherStore.voucherDetail();
      if (!voucherDetail) return;

      // show warning when voucher cannot be used for any associated product
      if (isEnergiegutschein(this.config().processType)) {
        // see if voucher is eligible for at least one product based on the filter
        const hasEligibleProduct = this.newVoucherStore.eligibleProducts().length > 0;

        if (!hasEligibleProduct) {
          const warning = createNewVoucherWarning('eligibilityTitle', 'eligibilityText', 'eligibilityCTA');
          untracked(() => this.newVoucherStore.updateVoucherDetailWarning(warning));
          return;
        }
      }

      this.voucherDetail.set(voucherDetail);
    });

    /** ===== Tarif-Change / New-Customer effects ===== **/
    // Whenever a correct tarif card changed, set it in this
    effect(() => {
      if (!isTarifwechselOrKundeWerden(this.config().processType)) return;

      const selectedTariffsForNewVoucher = this.newVoucherStore.tariffSelectionStore.selectedTariffsForNewVoucher();
      if (selectedTariffsForNewVoucher?.length === 0) return;

      this.selectedTariffs.set(selectedTariffsForNewVoucher);
    });

    // Whenever the selection changes, redo the request
    effect(() => {
      if (this.selectedTariffs()?.length > 0) {
        if (untracked(() => this.asyncLoadingFuncLock())) return;

        this.codeStatus.set('check');
        untracked(() => this.formRef()?.triggerDefaultFormInputSpinnerSubmit());
      }
    });

    /** ===== UI effects ===== **/
    // Whenever a voucher is valid, add to the validation messages
    effect(() => {
      const config = this.config();
      const voucherValidity = this.newVoucherStore.voucherValidity();

      if (!voucherValidity) return;

      if (isEnergiegutschein(config.processType)) {
        const validMessageKeys = untracked(() => this.validMessageKeys());
        if (validMessageKeys?.length === 0) this.validMessageKeys.update(keys => [...keys, voucherValidity.infoText]);
      }

      if (isTarifwechselOrKundeWerden(config.processType)) {
        const selectedTariff = untracked(() => this.newVoucherStore.selectedTariff());

        const text = selectedTariff
          ? capitalizeFirstLetter(selectedTariff.category) + DELIMITER + voucherValidity.infoText
          : voucherValidity.infoText;

        this.validMessageKeys.update(keys => [...keys, text]);
      }
    });

    // Whenever at least one tarif is valid, display result
    effect(() => {
      if (!validCodeValidity(this.codeValidity())) return;

      /** THESE ARE THE LAST LOGIC LINES OF CODE EXECUTED AFTER EVERYTHING IS SAID AND DONE **/
      /** COMMUNICATION WITH OTHER WIZARD SHOULD NOW OCCUR WITH newVoucherStore.voucher() **/
      /** BUT YOU SHOULD ONLY REACT TO newVoucherStore.isNewValid() **/
      this.codeStatus.set('delete');
      untracked(() => this.updateVoucher(this.createVoucher(), true));
      this.addCSS(true);
      this.newVoucherStore.updateIsVisible(false);
    });

    // Whenever an error occurs and all tarifs are not eligible, set the error
    effect(() => {
      if (!errorCodeValidity(this.codeValidity())) return;

      const errorCode = this.newVoucherStore.voucherDetailError()?.error?.code;
      if (!errorCode) return;
      if (untracked(() => this.isWienerLinienVoucher())) return;

      if (untracked(() => this.code())) this.codeStatus.set('delete');
      this.updateVoucher(undefined, false);
      this.addCSS(false);
      this.newVoucherStore.updateIsVisible(true);

      this.setFormError(errorCode);
    });

    // Whenever a warning occurs, set the warning
    effect(() => {
      if (!this.newVoucherStore.voucherDetailWarning()) return;

      this.codeValidity.set('warning');
      this.codeStatus.set('delete');
      this.updateVoucher(undefined, false);
    });

    /** ===== Utility effects ===== **/
    // Reset everything whenever this.code changes
    effect(() => {
      this.code(); // react to this

      this.codeStatus.set('check');
      this.codeValidity.set(undefined);
      this.voucherDetail.set(undefined);
      this.additionalFields.set(undefined);
      this.wienerLinienVoucherDetail.set(undefined);
      this.isWienerLinienVoucher.set(undefined);
      this.validMessageKeys.set([]);

      untracked(() => {
        this.newVoucherStore.updateVoucher(undefined); // unset value in process when user changes code
        this.newVoucherStore.updateIsNewValid(undefined);
        this.newVoucherStore.updateVoucherValidity(undefined);
        this.newVoucherStore.updateVoucherDetailError(undefined);
        this.newVoucherStore.updateVoucherDetailWarning(undefined);
        this.newVoucherStore.updateSetWLError(undefined);
        this.formRef()?.setError(CODE_CONTROL_NAME, undefined);
      });

      afterNextRender(() => this.removeCSS(), { injector: this.injector }); // TODO: When an error occurs, input is-valid for a split second - Improve this
    });
  }

  /** ===== HTML Functions ===== **/

  onFormChanged(formData: IFormModel): void {
    this.code.set(formData?.[CODE_CONTROL_NAME] ?? '');
  }

  onInputBlurred(): void {
    if (this.code() && checkCodeStatus(this.codeStatus())) this.formRef()?.triggerDefaultFormInputSpinnerSubmit(); // call asyncLoadingFunc of this.formModel
  }

  async onWienerLinienInfo(formData: IFormModel): Promise<void> {
    const additionalFields = createAdditionalFields(formData);
    this.additionalFields.set(additionalFields);
    this.newVoucherStore.updateIsNewValid(null);
    this.validMessageKeys.set([]);

    const code = this.code();
    const config = this.config();
    const selectedTariffs = this.selectedTariffs();

    if (!code) return;

    this.codeValidity.set('pending');

    if (isTarifwechselOrKundeWerden(config.processType)) {
      // TODO: For WL only do the necessary requests
      const body = createBody({ config, additionalFields, selectedTariff: selectedTariffs[0] });
      await this.newVoucherStore.getVoucherValidity(code, body);
    }

    afterNextRender(() => this.codeValidity.set(this.validMessageKeys()?.length > 0 ? 'valid' : 'error'), { injector: this.injector });
  }

  onCTAClick(): void {
    this.router.navigateByUrl(`/${ADD_PRODUCTS_ROUTE}/${ADD_PRODUCTS_SEARCH_ROUTE}`);
  }

  /** ===== Other Functions ===== **/

  private async getVoucherDetail(): Promise<void> {
    // async since loading animation
    await this.newVoucherStore.getVoucherDetail(this.code(), isEnergiegutschein(this.config().processType));
  }

  private async getVoucherValidity(): Promise<void> {
    // async since loading animation

    // all of this is required when switching tarifs
    this.validMessageKeys.set([]);
    this.newVoucherStore.updateVoucherDetailError(undefined);
    this.newVoucherStore.updateVoucherDetailWarning(undefined);
    this.formRef()?.setError(CODE_CONTROL_NAME, undefined);

    const code = this.code();
    const config = this.config();
    const additionalFields = this.additionalFields();

    if (!code) return;

    this.codeValidity.set('pending');

    if (isEnergiegutschein(config.processType)) {
      const promises = [];

      const eligibleProducts = this.newVoucherStore.eligibleProducts();

      for (const product of eligibleProducts) {
        const body = createBody({ config, additionalFields, product });
        promises.push(this.newVoucherStore.getVoucherValidity(code, body));
      }

      await Promise.all(promises); // run in parallel
    }

    if (isTarifwechselOrKundeWerden(config.processType)) {
      const selectedTariffs = this.selectedTariffs();

      for (const selectedTariff of selectedTariffs) {
        const body = createBody({ config, additionalFields, selectedTariff });
        await this.newVoucherStore.getVoucherValidity(code, body, selectedTariff); // otherwise race condition in rare cases
      }
    }

    // if at least one validation message exists, display it after all effects() from the last Promise concluded
    afterNextRender(
      () => {
        const atLeastOneValid = this.validMessageKeys()?.length > 0;

        if (atLeastOneValid) {
          const voucherDetail = this.voucherDetail();
          this.isWienerLinienVoucher.set(isWienerLinienVoucher(voucherDetail));

          if (this.isWienerLinienVoucher()) {
            this.newVoucherStore.updateVoucherValidity(undefined);
            this.newVoucherStore.updateVoucherValidityError(undefined);
            this.newVoucherStore.updateIsVisible(true);
            this.wienerLinienVoucherDetail.set(voucherDetail);

            this.codeStatus.set('delete');
            this.newVoucherStore.updateVoucher(undefined);
            this.newVoucherStore.updateIsNewValid(false);
            this.addCSS(true);
            return;
          }
        }

        this.codeValidity.set(atLeastOneValid ? 'valid' : 'error');
      },
      { injector: this.injector },
    );
  }

  private createVoucher(): IAktionen {
    const code = this.code();
    const config = this.config();
    const voucherDetail = this.voucherDetail();
    const additionalFields = this.additionalFields();
    const validMessageKeys = this.validMessageKeys();
    let commonMapping = config?.commonMapping();
    let key = undefined;
    let text = undefined;

    // I don't like BenefitsCardComponent. I think this component needs to be refactored otherwise this is necessary.
    if (isTarifwechselOrKundeWerden(config?.processType)) {
      key = validMessageKeys.map(key => key.split(DELIMITER)[0]);
      text = validMessageKeys.map(key => key.split(DELIMITER)[1]);
    }

    if (isEnergiegutschein(config?.processType)) {
      const eligibleProducts = this.newVoucherStore.eligibleProducts();
      const eligibleProductsIds = eligibleProducts.map(product => product.anlageId);
      const filteredMapping: CommonMappingData[] = commonMapping
        .map(cm => {
          const matchingAnlageId = cm.anlageIds.find(id => eligibleProductsIds.includes(id));
          if (!matchingAnlageId) return undefined;

          return {
            ...cm,
            validVoucherAnlageId: cm.validVoucherAnlageId ?? matchingAnlageId,
          };
        })
        .filter(cm => cm !== undefined);

      commonMapping = filteredMapping.length ? filteredMapping : undefined;
      text = validMessageKeys;
    }

    return {
      aktionscode: code,
      aktionsCodeArt: voucherDetail?.aktionsCodeArt,
      prozesstyp: config?.processType,
      infoText: text,
      infoTextKey: key,
      nennwert: voucherDetail?.nennwert,
      zusatzInformationen: additionalFields,
      commonMappingData: commonMapping,
      errorTitle: undefined, // TODO: Improve this
      errorMsg: undefined, // TODO: Improve this
    } as IAktionen;
  }

  private updateVoucher(voucher: IAktionen, isNewValid: boolean): void {
    // has to be untracked otherwise infinite loop
    untracked(() => this.newVoucherStore.updateVoucher(voucher));
    untracked(() => this.newVoucherStore.updateIsNewValid(isNewValid));
  }

  private setFormError(errorCode: ErrorType): void {
    const error = { [errorCode]: true } as ValidationErrors;
    this.formRef()?.setError(CODE_CONTROL_NAME, error);
  }

  private getAllErrors(): Record<ErrorType, string> {
    // error messages change depending on the configuration
    const processType = this.config().processType;

    return {
      ...ERRORS,
      3008: createProcessILError(processType, 3008),
      3009: createProcessILError(processType, 3009),
      3010: createProcessILError(processType, 3010),
      3011: createProcessILError(processType, 3011),
      3012: createProcessILError(processType, 3012),
      3018: createProcessILError(processType, 3018),
      3030: createProcessILError(processType, 3030),
    };
  }

  // So as far as I understand it, because I react to changes of this.formRef, but only want to display the actual validation
  // when the request logically concluded (with the ability to check or delete the input even though it might be invalid),
  // a unique work-around for correct visuals is necessary. This can, and probably should, be reworked. TODO: Improve this
  private addCSS(isValid: boolean): void {
    const inputHTMLElement = untracked(() => this.inputHTMLElement());
    inputHTMLElement?.classList.add(isValid ? 'is-valid' : 'is-invalid');
  }

  private removeCSS(): void {
    const inputHTMLElement = untracked(() => this.inputHTMLElement());
    inputHTMLElement?.classList.remove('is-valid', 'is-invalid');
  }

  /** ===== Signal Functions ===== **/

  // this can not be inside a computed - I tried and the error behaviour doesn't seem to work
  private initFormModel(): IFormModel {
    const config = this.config();
    const initialValue = config.predefinedCode ?? this.code();
    const errorItems = this.errorItems();

    return {
      inputs: [
        {
          name: CODE_CONTROL_NAME,
          initialValue: initialValue,
          labelKey: 'newVoucher.input.placeHolder',
          validate: true,
          validators: config.isCodeMandatory ? [Validators.required] : [],
          validateSuccess: true, // marks the input green if validators pass
          validationErrors: errorItems,
          loadingSpinnerOptions: {
            suppressValidityCheckOnSubmit: true, // this makes it so that pressing delete does something even though input is invalid
            enterInInputTriggersSpinnerButton: true, // this makes it so that pressing Enter calls asyncLoadingFunc
            // factories since these values get dynamically retrieved and should not be const
            disabled: () => this.codeCheckDisabled(),
            submitLabelKey: () => (deleteCodeStatus(this.codeStatus()) ? 'newVoucher.code.delete' : 'newVoucher.code.check'),
            // this function entails the main logic of checking or deleting the code
            asyncLoadingFunc: async (): Promise<void> => {
              const codeStatus = this.codeStatus();

              if (!this.code()) return this.newVoucherStore.causeIsNewValidUpdate();
              if (!codeStatus) return;

              if (deleteCodeStatus(codeStatus)) this.formRef()?.form.reset(); // custom reset as predefined code (initial value) may be set otherwise
              if (checkCodeStatus(codeStatus)) {
                this.removeCSS();
                this.newVoucherStore.updateIsNewValid(null);
                await this.getVoucherDetail(); // await since loading animation

                // So because this.getVoucherValidity relies on values from effects() from this, but they don't get instantly updated during
                // this.getVoucherDetail(), this work-around needs to be done.
                // This is mainly required because the validity predefined codes can be assessed before the available products
                // are fetched, which should not be the case. It is wrapped in a Promise for correct loading animation.
                return new Promise(resolve => {
                  afterNextRender(
                    async () => {
                      if (this.voucherDetail()) await this.getVoucherValidity();
                      resolve();
                    },
                    { injector: this.injector },
                  );
                });
              }
            },
          },
        },
      ],
    };
  }
}
